import { layout } from '../../../constants/urlconfig';
import { Typography } from '@material-tailwind/react';
import { FooterLinkIcon } from './FooterLink';
import ChannelService from '../../../api/ChannelService';
import { useAppDispatch } from '../../../store/hook';
import { TermModal, TermType } from '../../common/Term';
import { openModal } from '../../../store/features/modalSlice';
import { useCsInfo } from '../../../hooks/useCsInfo';
import { useSiteConfig } from '../../../hooks/useSiteConfig';
import {
  CompanyInfo,
  SITE_CONFIG_KEY,
} from '../../../constants/types/site-config-type';
import { parseSiteConfigObject } from '../../../api/utils/api/siteConfigApi';

/** 사이트 공통 푸터 (고객센터, SNS 링크, 사업자 정보, 약관) */
export const Footer = () => {
  return (
    <footer className="m-auto w-full max-w-[1440px] lg:px-14">
      <div className="mb-4 flex flex-col justify-between gap-4 lg:my-14 lg:flex-row-reverse">
        <div>
          <CS />
          <FooterLinks />
        </div>
        <Info />
      </div>
    </footer>
  );
};

// 사업자 정보는 법정 표시 사항이라 조회 실패 시에도 반드시 보이도록 기본값을 둔다
const COMPANY_INFO_FALLBACK: CompanyInfo = {
  name: '(주)캐더',
  address: '서울시 강남구 선릉로 338, 8,9층',
  ceo: '정학영',
  businessNumber: '220-87-79868',
  mailOrderNumber: '제2014-서울서초-1004호',
  copyright: 'Cather',
};

const SITE_CONFIG_KEYS = [SITE_CONFIG_KEY.COMPANY_INFO];

/** 회사/사업자 정보 + 약관 목록 */
const Info = () => {
  const { phone: contactPhone, email: csEmail } = useCsInfo();
  const { configs } = useSiteConfig(SITE_CONFIG_KEYS);
  // 어드민에서 일부 필드를 빠뜨려도 해당 필드만 기본값으로 채운다
  const company = {
    ...COMPANY_INFO_FALLBACK,
    ...parseSiteConfigObject<Partial<CompanyInfo>>(
      configs[SITE_CONFIG_KEY.COMPANY_INFO],
    ),
  };

  return (
    <div className="text-gray-07 text-sm">
      <div className="px-4 py-2">
        <Typography variant="h5" className="text-blue-gray-900 opacity-50">
          {company.name}
        </Typography>
        <Typography variant="h6" className="mt-2 font-normal">
          {company.address}
        </Typography>
        <Typography variant="h6" className="mb-8 font-normal">
          {csEmail} / {contactPhone}
        </Typography>
        <Typography variant="h6" className="font-normal">
          대표자명 : {company.ceo} / 사업자등록번호 : {company.businessNumber}
        </Typography>
        <Typography variant="h6" className="font-normal">
          통신판매업신고번호 : {company.mailOrderNumber}
        </Typography>
        <Typography variant="h6" className="font-medium">
          ⓒ {company.copyright} All rights reserved.
        </Typography>
      </div>
      <div className="lg:px-4 lg:py-2">
        <TermLinks />
      </div>
    </div>
  );
};

/** 약관 목록. 클릭 시 약관 모달 오픈 */
const TermLinks = () => {
  const dispatch = useAppDispatch();
  const termTypes = [
    TermType.SERVICE,
    TermType.PRIVACY_POLICY,
    TermType.REFUND_POLICY,
  ];
  const onClick = (termType: TermType) => {
    dispatch(openModal({ body: <TermModal termType={termType} /> }));
  };
  return (
    <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 lg:justify-start">
      {termTypes.map((item, index) => (
        <li
          key={index}
          onClick={() => onClick(item)}
          className="block cursor-pointer font-sans text-base font-bold leading-relaxed text-blue-gray-900 antialiased transition-colors hover:text-blue-500 focus:text-blue-500"
        >
          {item}
        </li>
      ))}
    </ul>
  );
};

/** 고객센터 연락처/운영시간 */
const CS = () => {
  const { phone: contactPhone, hours: supportHours } = useCsInfo();

  return (
    <div className="bg-purple-00 p-5 lg:mt-6 lg:bg-white lg:p-0">
      <Typography variant="h6" className="py-1 font-bold">
        고객센터 {contactPhone}
      </Typography>
      <Typography variant="h6" className="py-1 font-normal">
        {supportHours}
      </Typography>
      <Typography
        variant="h6"
        className="mt-4 hidden cursor-pointer font-normal"
        onClick={() => {
          ChannelService.openChat();
        }}
      >
        1:1 문의하기
      </Typography>
    </div>
  );
};

/** SNS 아이콘 링크 목록 */
const FooterLinks = () => (
  <div className="mt-6 px-4 py-2">
    <div className="flex flex-row items-center justify-center gap-x-5">
      <FooterLinkIcon
        to={layout.kakaochannel}
        view={'/svg/icon-kakaochannel.svg'}
      />
      <FooterLinkIcon to={layout.blog} view={'/svg/icon-blog.svg'} />
      <FooterLinkIcon to={layout.youtube} view={'/svg/icon-youtube.svg'} />
      <FooterLinkIcon to={layout.instagram} view={'/svg/icon-instagram.svg'} />
    </div>
  </div>
);
